import { Injectable } from '@angular/core';
import { User } from './models/user/user.model';
import { UserLocation } from './models/user/location.model';
import { environment } from '../environments/environment';
import { NavigationService } from './navigation.service';

declare global {
  var pendo: {
    initialize: (options: PendoOptions) => void;
    identify: (options: PendoOptions) => void;
    updateOptions: (options: Partial<PendoOptions>) => void;
    pageLoad: () => void;
    track: (eventName: string, metadata?: Record<string, unknown>) => void;
    _q: unknown[];
  };
}

interface PendoOptions {
  visitor: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    [key: string]: unknown;
  };
  account: {
    id: string;
    name?: string;
    [key: string]: unknown;
  };
}

@Injectable({
  providedIn: 'root'
})
export class PendoService {
  private initialized = false;
  private scriptLoaded = false;

  constructor(private navigationService: NavigationService) {}

  /**
   * Load the Pendo script dynamically
   */
  private loadPendoScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded) {
        resolve();
        return;
      }

      const apiKey = environment.pendoApiKey;
      if (!apiKey) {
        console.warn('[PendoService] Pendo API key not configured');
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        console.error('[PendoService] Failed to load Pendo script');
        reject(new Error('Failed to load Pendo script'));
      };

      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    });
  }

  /**
   * Initialize Pendo with user and account information
   */
  async initialize(user: User, location?: UserLocation): Promise<void> {
    if (this.initialized || !environment.pendoApiKey) {
      return;
    }

    try {
      await this.loadPendoScript();

      if (!globalThis.pendo) {
        return;
      }

      globalThis.pendo.initialize({
        visitor: {
          id: user.guid || user.userName,
          esl_mail: user.email,
          sl_firstName: user.firstName,
          sl_lastName: user.lastName,
          sl_userName: user.userName,
          sl_location_name: location.locationName,
          sl_location_number: location.locationNumber,
          sl_location_city: location?.address?.city,
          sl_location_state: location.address?.state,
          sl_user_id: user.userName,
          sl_user_audience: this.navigationService.getCfaAudience(),
          sl_user_role: user.userType,
        },
        account: {
          id: location?.locationNumber || 'unknown',
          name: location?.locationName || 'Unknown Location'
        }
      });

      this.initialized = true;
    } catch (error) {
      console.error('[PendoService] Error initializing Pendo:', error);
    }
  }

  /**
   * Update Pendo with new account/location information
   */
  updateAccount(location: UserLocation): void {
    if (!globalThis.pendo) {
      return;
    }

    globalThis.pendo.updateOptions({
      visitor: {} as PendoOptions['visitor'],
      account: {
        id: location.locationNumber,
        name: location.locationName
      }
    });
  }

  /**
   * Track a custom event in Pendo
   */
  track(eventName: string, metadata?: Record<string, unknown>): void {
    if (!globalThis.pendo) {
      return;
    }

    globalThis.pendo.track(eventName, metadata);
  }
}
